
import React from 'react';

import { Box } from '@mui/material';
/* eslint-disable no-mixed-spaces-and-tabs */
import Typography from '../components/typography/typography';
import { Document } from '../components/icons';
import DocumentPreview from '../components/document-preview/document-preview';
import { MaterialTableColumnProps, TypeBooleanLabels } from '../components/material-table/material-table';
import { TFunction } from 'i18next';
import { formatDate, formatUtcDate } from './dateFormat';
import { MRT_Row, MRT_RowData } from 'material-react-table';
import { Link } from 'react-router-dom';
import DefaultAggregation from '../components/material-table/components/default-aggregation';
import Chip from '../components/chip/chip';
import _ from 'lodash';
import images from '../assets/images';
import { defaultCurrencySymbol, formatAmount, formatAmountWithCurrency, toFixedWithNumbers, defaultCurrencyFormat } from "./common";

import ValueField from '../components/value-field/value-field';
import { formatLabel } from './format-text';
import { generateRouteWithId } from './route-utils';
import dayjs from 'dayjs';
import { useAuth } from '../contexts';
import { getApiConfigSafe } from './api-config';
const downloadFile = async (urlString, fileName) => {
	try {
		// Fetch the file from the provided URL
		const apiConfig = getApiConfigSafe();
		const s3BucketUrl = apiConfig?.s3BucketUrl || import.meta.env.VITE_S3_BUCKET_URL || '';
		const url = urlString.includes("http") ? urlString : s3BucketUrl + urlString;
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`Failed to fetch file: ${response.statusText}`);
		}

		// Convert the response to a blob
		const blob = await response.blob();

		// Create a temporary URL for the blob
		const blobUrl = window.URL.createObjectURL(blob);

		// Create a link element
		const link = document.createElement('a');
		link.href = blobUrl;
		link.download = fileName || 'downloaded-file'; // Fallback filename if none provided

		// Append link to the body, trigger click, and remove it
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		// Revoke the blob URL to free up memory
		window.URL.revokeObjectURL(blobUrl);
	} catch (error) {
		console.error('Error downloading file:', error);
		throw error; // Re-throw to allow caller to handle the error
	}
};

export const getFileNamesFromUrlsAsLink = (fileUrls: string | string[], mode?: string) => {
	const fUrls = fileUrls
		? Array.isArray(fileUrls)
			? fileUrls
			: [fileUrls]
		: [];

	if (fUrls && fUrls.length) {
		return (
			<Box
				display='flex'
				gap={1}
				mb={1}
				alignItems='center'
				flexDirection='column'>
				{fUrls.map((url) => {
					const fileName = url.substring(url.lastIndexOf('/') + 1);
					return (
						<Typography
							type='s3'
							weight='medium'
							color='#246DFF'
							sx={{ textDecoration: 'underline', textUnderlineOffset: '3px', cursor: 'pointer' }}
							noWrap
							onClick={() => mode === 'add' ? undefined : downloadFile(url, fileName)}>
							{fileName}
						</Typography>
					);
				})}
			</Box>
		);
	}

	return null;
};

export const getFileNamesFromUrls = (fileUrls: string[]) => {
	if (!fileUrls) return null;

	if (!fileUrls?.filter(Boolean)?.length) return null;

	return fileUrls?.filter(Boolean)?.map((url) => {
		const fileName = url?.substring(url?.lastIndexOf('/') + 1) || '';
		return (
			<Box display='flex' gap={1} mb={1} alignItems='center'>
				<Document fontSize='small' />
				<Typography
					type='s3'
					weight='medium'
					color={'theme.secondary.1000'}
					sx={{ cursor: 'pointer' }}
					noWrap onClick={() => downloadFile(url, fileName)}>
					{fileName}
				</Typography>
				<DocumentPreview fileUrl={url} />
			</Box>
		);
	});
};

export type CurrencySymbolFn = (row: MRT_Row<MRT_RowData>) => string;
export type StatusClassesFn = (row: MRT_Row<MRT_RowData>) => string;
export type RedirectionLinkFn = (row: MRT_Row<MRT_RowData>) => string;

export interface ITransformTableColumns {
	columns: MaterialTableColumnProps[];
	currencySymbol?: string | CurrencySymbolFn;
	translationFn?: TFunction<'translation', undefined>;
	enableFooter?: boolean;
	redirectionLink?: string | RedirectionLinkFn;
	redirectionLinkState?: (row: MRT_Row<MRT_RowData>) => void;
	redirectionPathWithId?: string;
	idField?: string;
	customizeValue?: (row: MRT_Row<MRT_RowData>, columnAccessorKey?: string, value?: any) => any;
	statusClasses?: string | StatusClassesFn;
	rows: any[];
	currencyFormat?: boolean;
	fraction?: number;
}

const jsonArrayToString = (json: any) => {
	if (!json) return '';

	try {
		return JSON.parse(json)?.join(', ');
	} catch (error) {
		return '';
	}
};
const getStatusClass = (status: any) => {
	if (typeof status === 'string') {
		const s = status?.toLowerCase()?.replaceAll(' ', '');
		return s
	} else if (typeof status === 'number') {
		return status ? 'enabled' : 'disabled';
	}
};

export const transformTableColumns = ({
	columns = [],
	currencySymbol,
	translationFn = undefined,
	enableFooter = false,
	redirectionLink = '',
	redirectionLinkState = undefined,
	redirectionPathWithId = '',
	idField = 'id',
	statusClasses = '',
	rows = [],
	customizeValue = undefined,
	currencyFormat,
	fraction
}: ITransformTableColumns) => {
	const currencyFormatData = currencyFormat ? currencyFormat : defaultCurrencyFormat()

	return columns.map((column) => ({
		...column,
		header: translationFn ? translationFn(column.header) : column.header,
		Cell: column.Cell ? column.Cell : ({ renderedCellValue, row }: any) => {
			let v = renderedCellValue;
			const columnType = column.type || (column.header === "Amount" || column.header === "Rate" ? "currency" : undefined);
			const isAmount = ['currency', 'amount'].includes(columnType);
			if (typeof v === 'number' && v && String(v)?.includes('.')) {
				return v = isAmount ? formatAmount(v, currencyFormatData, 'before', currencySymbol instanceof Function ? currencySymbol(row) : currencySymbol, fraction) : toFixedWithNumbers(v, fraction);
			}
			switch (columnType) {
				case 'formattedString':
					v = formatLabel(renderedCellValue) || '-';
					break;
				case 'date':{
					console.log("renderedCellValue d.....", renderedCellValue)
					v = formatUtcDate(
						renderedCellValue,
						column.editProperties?.dateFormat || 'DD-MM-YYYY'
					);
					break;
				}
				case 'datetime':
					{
						console.log("renderedCellValue dt.....", renderedCellValue)
						v = formatUtcDate(
						renderedCellValue,
						column.editProperties?.dateFormat || 'DD-MM-YYYY hh:mm A'
					);
					break;
				}
				case 'currency':
				case 'amount': {
					const cs = column?.showDefaultCurrency ? defaultCurrencySymbol() : currencySymbol instanceof Function ? currencySymbol(row) : (currencySymbol || defaultCurrencySymbol());
					v = renderedCellValue || renderedCellValue == 0
						? `${formatAmount(renderedCellValue, currencyFormatData, 'before', cs, fraction) || '-'}`
						: null;

				}
					break;
				case 'status':
				case 'enum':
				case 'array':
					{
						v = renderedCellValue || renderedCellValue === 0 ? (
							<>
								{Array.isArray(renderedCellValue) ? (
									<>
										{renderedCellValue?.length ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
											{renderedCellValue.map((rcv) => (
												<Chip
													type='normal'
													className={column.custom_class ? column?.custom_class : (typeof rcv === 'string' ? rcv : '')}
													label={
														<Typography type='s4' weight='medium' color={'inherit'}>
															{typeof rcv === 'object' ? _.get(rcv, column?.typeArrayAccessorKey || '', '') : rcv}
														</Typography>
													}
												/>
											))}
										</Box> : '-'}
									</>
								) : (
									<Chip
										type='normal'
										className={
											column.custom_class ? column?.custom_class :
												(statusClasses instanceof Function
													? statusClasses(row)
													: `${statusClasses}${getStatusClass(renderedCellValue)}`)
										}
										label={

											<Typography type='s4' weight='medium' color={'inherit'}>
												{renderedCellValue}
											</Typography>
										}
									/>
								)}
							</>
						) : (
							'-'
						);
					}
					break;
				case 'boolean':
					v = (
						<Chip
							type='normal'
							// active={Boolean(renderedCellValue)}
							// inActive={Boolean(!renderedCellValue)}
							sx={(theme) => ({
								...(renderedCellValue ?
									{
										backgroundColor: theme.palette?.theme.tertiary4[200],
										color: theme.palette?.theme.tertiary4[900]
									} :
									{
										backgroundColor: theme.palette?.theme.tertiary6[200],
										color: theme.palette?.theme.tertiary6[900]
									}
								)
							}
							)}
							label={
								<Typography type='s4' weight='medium' color={'inherit'}>
									{column?.typeBooleanLabels?.[renderedCellValue as keyof TypeBooleanLabels] || '-'}
								</Typography>
							}
						/>
					);
					break;
				case 'jsonArray':
					v = jsonArrayToString(renderedCellValue);
					break;
				case 'custom':
				case 'custom_link':
					v = customizeValue?.(row, column.accessorKey, renderedCellValue);
					break;
				case 'toFixed':
					v = toFixedWithNumbers(renderedCellValue)
					break;
			}

			// Allow customizeValue to override any type's default rendering
			// if (customizeValue) {
			// 	const customResult = customizeValue(row, column.accessorKey, renderedCellValue);
			// 	if (customResult !== undefined) {
			// 		v = customResult;
			// 	}
			// }

			// If redirectionPathWithId is provided, use it for URL parameter-based navigation
			if (redirectionPathWithId && column.type != 'custom_link') {
				const id = row.original[idField || 'id'];
				if (id) {
					const path = redirectionPathWithId instanceof Function ? generateRouteWithId(redirectionPathWithId(row), id?.toString()) : generateRouteWithId(redirectionPathWithId, id?.toString());
					return (
						<Link to={path}>
							{v}
						</Link>
					);
				}
			}
			// Fall back to the existing redirection logic
			else if (redirectionLink && column.type != 'custom_link') {
				const id = row.original[idField || 'id'];
				const path = redirectionLink instanceof Function ? generateRouteWithId(redirectionLink(row), id?.toString()) : generateRouteWithId(redirectionLink, id?.toString());
				return (
					<Link to={path}
						state={redirectionLinkState?.(row)}>
						{v}
					</Link>
				);
			}
			return v;
		},
		...(Boolean(enableFooter) && {
			Footer: ({ column }) => (
				<DefaultAggregation data={rows} column={String(column.id)} />
			)
		})
	}));
};

export const renderEmptyRowsFallback = (text: string) => (
	<Box className='no-data'>
		<img src={images.common.tableNoData} />
		<Typography type='s3' weight='medium' color='theme.secondary.1000'>
			{text}
		</Typography>
	</Box>
)

/* eslint-disable no-mixed-spaces-and-tabs */
export const renderValueField = (label: string, value: any, t: any, formatFn?: (val: any) => string) => {
	return (
		<ValueField key={label} label={t?.(label)}>
			{formatFn ? formatFn(value) : value ?? '-'}
		</ValueField>
	);
};

export const formateValueByType = ({
	value,
	type,
	format,
	weight
}: {
	value: any;
	type: string;
	format?: string;
	weight?: "normal" | "medium" | "bold";
}) => {
	const numberTypes = ['number', 'amount', 'toFixed'];
	const valueCheck = [null, undefined, '0'];
	if (valueCheck.includes(value)) return <Typography type='s4' weight={weight} color='theme.neutral.1000'>{numberTypes?.includes(type) ? value : (value || '-')}</Typography>;

	switch (type) {
		case 'date':
			return dayjs(value).isValid() ? formatDate(value, format) : value;
		case 'toFixed':
			return <Typography type='s4' weight={weight} color='theme.neutral.1000'>{value ? toFixedWithNumbers(Number(value)) : '-'}</Typography>;
		case 'amount':
			return <Typography type='s4' weight={weight} color='theme.neutral.1000'>{value ? formatAmountWithCurrency(Number(value)) : '-'}</Typography>;
		default:
			return weight ? <Typography type='s4' weight={weight} color='theme.neutral.1000'>{value || '-'}</Typography> : value || '-';
	}
};


const setCompany = (value: any) => {
	const { user } = useAuth();
	return {
		value: user?.company_id,
		options: [{
			label: user?.company_data?.name,
			value: user?.company_id
		}]
	}
}
export const configMapper = {
	'company': {
		callBack: setCompany,

	}
}
